import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import apiClient from '../utils/apiClient';
import Logo from '../components/Logo';

export default function RegisterPage() {
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await apiClient.post('/auth/register/', form);
      if (plan) {
        navigate(`/login?plan=${plan}`);
      } else {
        navigate('/login');
      }
    } catch (err) {
      const data = err.response?.data;
      setError(typeof data === 'object' ? JSON.stringify(data) : 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', boxSizing: 'border-box',
    background: '#0D1117', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, fontSize: 14, outline: 'none',
    color: 'white', fontFamily: 'Inter, sans-serif',
    transition: 'border-color 0.15s',
  };

  const handleFocus = (e) => { e.target.style.borderColor = '#F97316'; };
  const handleBlur = (e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; };

  const shellLink = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#9CA3AF',
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 600,
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#0D1117',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Inter, sans-serif', padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16, gap: 12,
        }}>
          <Link to="/" style={shellLink}>
            <ArrowLeft size={15} /> Home
          </Link>
          <Link to="/login" style={{ ...shellLink, color: '#F97316' }}>
            Already registered? Sign in
          </Link>
        </div>

        <div style={{
          background: '#1A2236', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20, padding: '40px 36px', width: '100%',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <Logo size={48} />
            </div>
            <h1 style={{
              fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: 26,
              color: 'white', margin: '0 0 8px', letterSpacing: '-0.02em',
            }}>
              Multi <span style={{ color: '#F97316' }}>Author</span>
            </h1>
            <p style={{ color: '#6B7280', fontSize: 14, margin: 0 }}>
              Create your workspace account
            </p>
          </div>

          {error && (
            <div style={{
              background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.3)',
              color: '#F87171', borderRadius: 10, padding: '10px 16px',
              fontSize: 13, marginBottom: 20,
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {[
              { key: 'username', label: 'Username', type: 'text', placeholder: 'your username' },
              { key: 'email', label: 'Email', type: 'email', placeholder: 'you@company.com' },
              { key: 'password', label: 'Password', type: 'password', placeholder: '••••••••' },
            ].map(({ key, label, type, placeholder }) => (
              <div key={key} style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#9CA3AF', marginBottom: 6 }}>
                  {label}
                </label>
                <input
                  type={type}
                  value={form[key]}
                  onChange={e => setForm({ ...form, [key]: e.target.value })}
                  placeholder={placeholder}
                  required
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </div>
            ))}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px', marginTop: 12,
                background: loading ? '#C2550A' : '#F97316',
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Syne, sans-serif', letterSpacing: '-0.01em',
                transition: 'background 0.15s',
              }}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 22, fontSize: 13, color: '#6B7280' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#F97316', fontWeight: 600, textDecoration: 'none' }}>
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
